<template>
  <div class="purchase-page">
    <PurchaseTabs 
      v-if="tabs && tabs.length > 0"
      :active-tab="activeTab" 
      :tabs="tabs"
      @change="handleTabChange" 
    />
    
    <PurchaseSearchForm 
      v-if="searchFields.length > 0"
      v-model="internalSearchForm"
      :total-count="totalCount"
      :total-count-label="totalCountLabel"
      :show-total-count="showTotalCount"
      :show-time-filter="showTimeFilter"
      :search-fields="searchFields"
      :time-filter-tabs="timeFilterTabs"
      @search="handleSearch"
      @reset="handleReset"
    />
    
    <div class="table-box">
      <PurchaseTable 
        :table-data="tableData"
        :loading="loading"
        :page-info="pageInfo"
        :columns="columns"
        :show-expand="showExpand"
        :expand-columns="expandColumns"
        :show-operation="showOperation"
        :operation-width="operationWidth"
        :operation-fixed="operationFixed"
        @expand-change="handleExpandChange"
        @view="handleView"
        @cancel="handleCancel"
        @product-action="handleProductAction"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        @refresh="getList"
      >
        <template #toolbarLeft>
          <slot name="toolbarLeft"></slot>
        </template>
        <template #toolbarRight>
          <slot name="toolbarRight"></slot>
        </template>
        
        <!-- 主表格列插槽 -->
        <template v-for="col in columns" :key="col.prop" #[col.prop]="{ row, column }">
          <slot :name="col.prop" :row="row" :column="column"></slot>
        </template>
        
        <!-- 展开行列插槽 -->
        <template v-for="col in expandColumns" :key="`expand-${col.prop}`" #[`expand-${col.prop}`]="{ row, column }">
          <slot :name="`expand-${col.prop}`" :row="row" :column="column"></slot>
        </template>
        
        <!-- 操作列插槽 -->
        <template #operation="{ row }">
          <slot name="operation" :row="row"></slot>
        </template>
      </PurchaseTable>
    </div>
  </div>
</template>

<script>
import PurchaseTabs from './PurchaseTabs.vue';
import PurchaseSearchForm from './PurchaseSearchForm.vue';
import PurchaseTable from './PurchaseTable.vue';

export default {
  name: 'PurchasePage',
  components: {
    PurchaseTabs,
    PurchaseSearchForm,
    PurchaseTable
  },
  props: {
    tabs: {
      type: Array,
      default: () => []
    },
    showTotalCount: {
      type: Boolean,
      default: true
    },
    totalCountLabel: {
      type: String,
      default: '总采购单数'
    },
    showTimeFilter: {
      type: Boolean,
      default: true
    },
    searchFields: {
      type: Array,
      default: () => []
    },
    timeFilterTabs: {
      type: Array,
      default: () => [
        { label: '今天', value: 'today' },
        { label: '本周', value: 'week' },
        { label: '本月', value: 'month' },
      ]
    },
    searchForm: {
      type: Object,
      default: () => ({})
    },
    columns: {
      type: Array,
      default: () => []
    },
    showExpand: {
      type: Boolean,
      default: false
    },
    expandColumns: {
      type: Array,
      default: () => []
    },
    showOperation: {
      type: Boolean,
      default: false
    },
    operationWidth: {
      type: String,
      default: '150'
    },
    operationFixed: {
      type: String,
      default: 'right'
    },
    query: {
      type: Function,
      default: null
    },
    statusMap: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      activeTab: 'ALL',
      internalSearchForm: {},
      tableData: [],
      loading: false,
      totalCount: 0,
      pageInfo: {
        pageNum: 1,
        pageSize: 20,
        total: 0,
      },
    };
  },
  methods: {
    handleTabChange(tab) {
      this.activeTab = tab;
      this.pageInfo.pageNum = 1;
      this.$emit('tab-change', tab);
      this.getList();
    },
    handleSearch(formData) {
      this.internalSearchForm = { ...formData };
      this.$emit('update:searchForm', this.internalSearchForm);
      this.pageInfo.pageNum = 1;
      this.getList();
    },
    handleReset(formData) {
      this.internalSearchForm = { ...formData };
      this.$emit('update:searchForm', this.internalSearchForm);
      this.pageInfo.pageNum = 1;
      // 重置时不改变当前tab，只清空搜索条件
      this.getList();
    },
    handleExpandChange(row, expandedRows) {
      this.$emit('expand-change', row, expandedRows);
    },
    handleView(row) {
      this.$emit('view', row);
    },
    handleCancel(row) {
      this.$emit('cancel', row);
    },
    handleProductAction({ orderRow, productRow, action }) {
      this.$emit('product-action', { orderRow, productRow, action });
    },
    handleSizeChange(val) {
      this.pageInfo.pageSize = val;
      this.getList();
    },
    handleCurrentChange(val) {
      this.pageInfo.pageNum = val;
      this.getList();
    },
    async getList() {
      if (this.query) {
        this.loading = true;
        try {
          const params = {
            ...this.internalSearchForm,
            pageNo: this.pageInfo.pageNum,
            pageSize: this.pageInfo.pageSize,
          };
          
          // 只有在有 tabs 配置时才添加 statusTag 参数
          if (this.tabs && this.tabs.length > 0) {
            const statusTag = this.getStatusByTab(this.activeTab);
            params.statusTag = statusTag;
          }
          
          const res = await this.query(params);
          const { code, data } = res.data;
          if (code === '0') {
            this.tableData = data.rows || [];
            this.pageInfo.total = data.totalCount || 0;
            this.totalCount = data.totalCount || 0;
          }
        } catch (error) {
          console.error('获取列表失败:', error);
        } finally {
          this.loading = false;
        }
      }
    },
    getStatusByTab(tab) {
      if (this.statusMap && Object.keys(this.statusMap).length > 0) {
        return this.statusMap[tab] || '';
      }
      return '';
    },
    refresh() {
      this.getList();
    }
  },
  created() {
    this.internalSearchForm = { ...this.searchForm };
    this.getList();
  },
  watch: {
    searchForm: {
      handler(val) {
        this.internalSearchForm = { ...val };
      },
      deep: true
    }
  }
};
</script>

<style scoped lang="less">
.purchase-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  
  .table-box {
    flex: 1;
    padding: 0 20px;
    overflow: auto;
  }
}
</style>
